import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { Dispatch, ReactNode, SetStateAction, useState } from "react";
import Flex from "../../../../../../../components/Flex";
import Text from "../../../../../../../components/Text";
import { Option } from "../../../../../../../constants/common";
import { AccordionRecord } from "../../../../../../../components/app/provider/patient/chart";
// import ActionableHoverNew from "./ActionableHoverNew";
import CallMadeIcon from "@mui/icons-material/CallMade";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useRoleController } from "../../../../../../../components/useRoleController";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../hooks/use-query-param";
import { useReduceDisclosure } from "../../../../../../../hooks/use-reduce-disclosure";
import { useExportHistoryToEncounter } from "../../../../../../../queries/encounter/use-export-history-to-encounter";
import { useExportSocialHistoryToEncounter } from "../../../../../../../queries/encounter/use-export-social-history-to-encounter";
import {
  DEFAULT_ICON_COLOR,
  replaceUnderScoreAddSpace,
  showToastOnApiCall,
} from "../../../../../../../utils/utils";
import UpsertAllergiesDrawer from "../allergy/UpsertAllergiesDrawer";
import UpsertDiagnosesDrawer from "../diagnoses/UpsertDiagnosisDrawer";
import Allergies from "./encounterSummaryComponents/Allergies";
import Care from "./encounterSummaryComponents/Care";
import Diagnosis from "./encounterSummaryComponents/Diagnosis";
import History from "./encounterSummaryComponents/History";
import Medication from "./encounterSummaryComponents/Medication";
import VaccinesRecord from "./encounterSummaryComponents/VaccinesRecord";
import Vitals from "./encounterSummaryComponents/Vitals";
import UpsertMedicationsDrawer from "../medication/UpsertMedicationsDrawer";
import UpsertVaccineDrawer from "../vaccines/UpsertVaccineDrawer";
import UpsertVitalFormDrawerNew from "../vitals/UpsertVitalFormDrawerNew";
import UpsertCarePlanDrawer from "../care-plan/UpsertCarePlanDrawer";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useQueryClient } from "@tanstack/react-query";

interface PatientEncounterSummeryProps {
  accordionExpanded: boolean[];
  setAccordionExpanded: Dispatch<SetStateAction<boolean[]>>;
  SECTIONS: { label: string }[];
  allergyList: any;
  diagnosisList: any;
  vitalsList: any;
  medicationsList: any;
  vaccinesList: any;
  carePlanList: any;
  medicalHistoryList: any;
  surgicalHistoryList: any;
  familyHistoryList: any;
  loadingStates: any;
}

type SectionKey =
  | "Allergies"
  | "Diagnosis"
  | "Medications"
  | "Vaccines"
  | "Vitals"
  | "History"
  | "Care_Plan";

const PatientRecords = ({
  accordionExpanded,
  setAccordionExpanded,
  SECTIONS,
  allergyList,
  diagnosisList,
  vitalsList,
  medicationsList,
  vaccinesList,
  carePlanList,
  medicalHistoryList,
  loadingStates,
  surgicalHistoryList,
  familyHistoryList,
}: PatientEncounterSummeryProps) => {
  const toggleAccordion = (index: number) => {
    const newAccordionState = [...accordionExpanded];
    newAccordionState[index] = !newAccordionState[index];
    setAccordionExpanded(newAccordionState);
  };

  const navigate = useNavigate();
  const location = useLocation();

  const { patientId } = useParams();
  const appointmentId = useQueryParams("appointmentid");

  const { mutateAsync: exportHistoryToEncounter } =
    useExportHistoryToEncounter();
  const { mutateAsync: exportSocialHistoryToEncounter } =
    useExportSocialHistoryToEncounter();

  const handleNavigate = (label: string) => {
    const section = label?.toLowerCase();

    // During an ongoing encounter (visit note flow) the records sidebar is
    // shown alongside the encounter panel. Navigating to a different route
    // here would trip the navigation guard and prompt "End this encounter?".
    // Instead, keep the encounter active and just switch the encounter's left
    // panel to the corresponding section by updating the query params
    // (activePin drives the panel; clinicalSection lands on the right tab).
    const isVisitNote = location.pathname.includes("/visit-note");
    if (isVisitNote) {
      const params = new URLSearchParams(location.search);
      if (label === "History") {
        params.set("activePin", "history");
      } else {
        params.set("activePin", "clinical-details");
        // Tab value matches the lowercased label; Allergies is the default
        // first tab ("").
        params.set("clinicalSection", label === "Allergies" ? "" : section);
      }
      navigate(`${location.pathname}?${params.toString()}`);
      return;
    }

    const baseUrl = `/app/provider/patients/new-patient/${patientId}`;

    const urlPaths: Record<string, string> = {
      History: `/${section}`,
      Allergies: `/clinical-details`,
    };

    const url = baseUrl + (urlPaths[label] || `/clinical-details/${section}`);
    navigate(url);
  };

  const contentMapper: Record<string, ReactNode> = {
    Allergies: (
      <Allergies allergyList={allergyList} loading={loadingStates.Allergies} />
    ),
    Diagnosis: (
      <Diagnosis
        diagnosisList={diagnosisList}
        loading={loadingStates.Diagnosis}
      />
    ),
    Medications: (
      <Medication
        medicationList={medicationsList}
        loading={loadingStates.Medications}
      />
    ),
    Vaccines: (
      <VaccinesRecord
        vaccinesList={vaccinesList}
        loading={loadingStates.Vaccines}
      />
    ),
    Vitals: <Vitals vitalsList={vitalsList} loading={loadingStates.Vitals} />,
    History: (
      <History
        medicalHistoryList={medicalHistoryList}
        surgicalHistoryList={surgicalHistoryList}
        familyHistoryList={familyHistoryList}
        medicalHistoryLoading={loadingStates.History}
        surgicalHistoryLoading={loadingStates.Surgical_History}
        familyHistoryLoading={loadingStates.Family_History}
      />
    ),
    Care_Plan: (
      <Care carePlanList={carePlanList} loading={loadingStates.Care_Plan} />
    ),
  };

  const [
    allergiesDisclosure,
    upsertDiagnosisDisc,
    medicationsDisclosure,
    vaccinesDisclosure,
    vitalsDisclosure,
    historyDisclosure,
    carePlanDisclosure,
  ] = useReduceDisclosure(8);

  const AddDrawerDisclosureMapper: Record<SectionKey, DisclosureHost> = {
    Allergies: allergiesDisclosure,
    Diagnosis: upsertDiagnosisDisc,
    Medications: medicationsDisclosure,
    Vaccines: vaccinesDisclosure,
    Vitals: vitalsDisclosure,
    History: historyDisclosure,
    Care_Plan: carePlanDisclosure,
  };

  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const exportToNoteHistoryHandler = () => {
    if (patientId && appointmentId) {
      const exportPayload = {
        patientUuid: patientId,
        appointmentUuid: appointmentId,
      };
      showToastOnApiCall({
        showSnackbar,
        // Export both medical/surgical/family history and social history to the
        // encounter in parallel; resolve to the history response so the success
        // toast reflects the primary export.
        apiCall: Promise.all([
          exportHistoryToEncounter(exportPayload),
          exportSocialHistoryToEncounter(exportPayload),
        ]).then(([historyRes]) => historyRes),
        onSave: () => {
          queryClient.invalidateQueries({
            queryKey: ["patient-medical-history-list"],
          });
          queryClient.invalidateQueries({
            queryKey: ["patient-surgical-history-list"],
          });
          queryClient.invalidateQueries({
            queryKey: ["patient-family-history-list"],
          });
          queryClient.invalidateQueries({
            queryKey: ["all-patient-social-history-by-appointment-id"],
          });
        },
      });
    }
  };

  const EXCLUDE_ADD_BUTTON_FOR_SECTIONS = ["History"];

  return (
    <Flex flexDirection={"column"} width={"100%"}>
      <UpsertAllergiesDrawer disclosureHost={allergiesDisclosure} />
      <UpsertDiagnosesDrawer disclosureHost={upsertDiagnosisDisc} />
      <UpsertMedicationsDrawer disclosureHost={medicationsDisclosure} />
      <UpsertVaccineDrawer disclosureHost={vaccinesDisclosure} />
      <UpsertVitalFormDrawerNew disclosureHost={vitalsDisclosure} />
      <UpsertCarePlanDrawer
        curFormState={"add"}
        disclosureHost={carePlanDisclosure}
      />

      {SECTIONS.map((section, ind) => (
        <AccordionRecord
          key={ind}
          title={replaceUnderScoreAddSpace(section.label)}
          expanded={accordionExpanded[ind]}
          onToggle={() => toggleAccordion(ind)}
          hideAddButton={EXCLUDE_ADD_BUTTON_FOR_SECTIONS.includes(
            section.label
          )}
          onAdd={() =>
            AddDrawerDisclosureMapper[section.label as SectionKey]?.open()
          }
          rightActions={
            accordionExpanded[ind] ? (
              <Flex gap={0.2} alignItems={"center"} justifyContent={"flex-end"}>
                {(medicalHistoryList?.data?.content?.length > 0 ||
                  surgicalHistoryList?.data?.content?.length > 0 ||
                  familyHistoryList?.data?.content?.length > 0) &&
                  section.label === "History" && (
                    <Button
                      sx={{ borderRadius: 1, height: "22px" }}
                      onClick={() => exportToNoteHistoryHandler()}
                    >
                      Export to Note
                    </Button>
                  )}
                <Tooltip title="View All">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigate(section.label);
                    }}
                  >
                    <CallMadeIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Flex>
            ) : undefined
          }
        >
          {contentMapper[section.label]}
        </AccordionRecord>
      ))}
    </Flex>
  );
};

export default PatientRecords;

interface RightActionProps<T extends Option> {
  handleClickOption?: (v: T["value"]) => void;
  menuList: Option[];
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
}

const RightAction = <T extends Option>({
  menuList,
  handleClickOption,
  onMenuOpen,
  onMenuClose,
}: RightActionProps<T>) => {
  const [actionButton, setActionButton] = useState<HTMLElement | null>(null);
  const open = Boolean(actionButton);

  const handleActionButton = (event: React.MouseEvent<HTMLElement>) => {
    setActionButton(event.currentTarget);
    onMenuOpen?.();
  };

  const handleCloseActionButton = () => {
    setActionButton(null);
    onMenuClose?.();
  };
  const { hasReadOnly: hasReadOnlyClinical } = useRoleController("Clinical");

  return (
    <Box>
      {!hasReadOnlyClinical && (
        <Button
          size="small"
          sx={{ padding: 0, minWidth: 0, width: 30, height: 30 }}
          onClick={handleActionButton}
        >
          <MoreHorizIcon sx={{ fontSize: 15, color: DEFAULT_ICON_COLOR }} />
        </Button>
      )}
      <Menu
        anchorEl={actionButton}
        open={open}
        onClose={handleCloseActionButton}
        elevation={1}
      >
        {menuList?.map((it) => (
          <MenuItem
            key={it?.value}
            disabled={it?.isDisabled}
            onClick={() => {
              handleClickOption?.(it?.value);
              handleCloseActionButton();
            }}
          >
            <Text>{it?.label}</Text>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
export { RightAction };
